import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Updated nav order: Home | About | Services | Projects | Achievements | Gallery | Careers
  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/services', label: 'Services', hasDropdown: true, dropdownType: 'services' },
    { path: '/projects', label: 'Projects' },
    { path: '/achievements', label: 'Achievements', hasDropdown: true, dropdownType: 'achievements' },
    { path: '/gallery', label: 'Gallery' },
    { path: '/careers', label: 'Careers' }
  ];

  const servicesDropdown = [
    { label: 'EPC Contracts', path: '/services#epc' },
    { label: 'Renewable Energy', path: '/services#renewable' },
    { label: 'Substation Works', path: '/services#substation' },
    { label: 'Electrical & Instrumentation', path: '/services#electrical-instrumentation' },
    { label: 'Transmission Lines', path: '/services#transmission' },
    { label: 'Cogen & Sugar', path: '/services#cogen-sugar' }
  ];

  const achievementsDropdown = [
    { label: 'Awards & Recognition', path: '/awards' }
  ];

  const handleNavClick = (path) => {
    setIsMobileMenuOpen(false);
    navigate(path);
    window.scrollTo(0, 0);
  };

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white shadow-md' : 'bg-white'
    }`}
    style={isScrolled ? { boxShadow: '0 2px 10px rgba(0,0,0,0.1)' } : {}}
    >
      {/* Main Navigation */}
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex justify-between items-center">
          {/* Left Side: Logo + Navigation Links */}
          <div className="flex items-center gap-8">
            {/* Logo */}
            <Link to="/" className="flex items-center group" onClick={() => window.scrollTo(0, 0)}>
              <img
                src="https://customer-assets.emergentagent.com/job_industrial-epc-works/artifacts/7eu7rhsm_Calspark%20JPG.jpg"
                alt="Calspark Electrical & Engineers"
                className="h-20 w-20 object-contain transition-transform duration-300 group-hover:scale-105"
              />
            </Link>

            {/* Desktop Navigation Links - beside logo */}
            <div className="hidden lg:flex items-center gap-6">
              {navLinks.map((link) => {
                const dropdownItems = link.dropdownType === 'services' ? servicesDropdown : 
                                      link.dropdownType === 'achievements' ? achievementsDropdown : [];
                return (
                <div key={link.path} className="relative group">
                  {link.hasDropdown ? (
                    <>
                      <span
                        className={`text-base font-medium transition-all duration-300 relative py-2 cursor-pointer ${
                          location.pathname === link.path || location.pathname.startsWith('/awards')
                            ? 'text-teal-600'
                            : 'text-slate-700 hover:text-teal-600'
                        }`}
                      >
                        {link.label}
                        <svg className="w-4 h-4 inline-block ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </span>
                      {/* Dropdown Menu */}
                      <div className="absolute left-0 mt-2 w-56 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
                        <div className="bg-white rounded-lg shadow-xl border border-slate-200 py-2">
                          {dropdownItems.map((item, idx) => (
                            <Link
                              key={idx}
                              to={item.path}
                              onClick={() => window.scrollTo(0, 0)}
                              className="block px-4 py-2 text-base text-slate-700 hover:bg-teal-50 hover:text-teal-600 transition-colors"
                            >
                              {item.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    <Link
                      to={link.path}
                      onClick={() => window.scrollTo(0, 0)}
                      className={`text-base font-medium transition-all duration-300 relative py-2 ${
                        location.pathname === link.path
                          ? 'text-teal-600'
                          : 'text-slate-700 hover:text-teal-600'
                      }`}
                    >
                      {link.label}
                      {location.pathname === link.path && (
                        <span className="absolute bottom-0 left-0 w-full h-0.5 bg-teal-600"></span>
                      )}
                    </Link>
                  )}
                </div>
              );})}
            </div>
          </div>

          {/* Right Side: Contact Us + Social Icons */}
          <div className="hidden lg:flex items-center gap-5">
            <Link
              to="/contact"
              onClick={() => window.scrollTo(0, 0)}
              className={`text-base font-medium transition-all duration-300 py-2 ${
                location.pathname === '/contact'
                  ? 'text-teal-600'
                  : 'text-slate-700 hover:text-teal-600'
              }`}
            >
              Contact Us
            </Link>
            <div className="flex items-center gap-3 pl-4 border-l border-slate-300">
              {/* LinkedIn */}
              <a href="https://www.linkedin.com/company/calspark-electrical-and-engineers/" target="_blank" rel="noopener noreferrer" className="text-slate-600 hover:text-teal-600 transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                </svg>
              </a>
              {/* Facebook */}
              <a href="https://www.facebook.com/CalsparkElectricalEngineers/" target="_blank" rel="noopener noreferrer" className="text-slate-600 hover:text-teal-600 transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              </a>
              {/* YouTube - no link */}
              <span className="text-slate-600 cursor-default">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </span>
              {/* X (formerly Twitter) - no link */}
              <span className="text-slate-600 cursor-default">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </span>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-slate-700 hover:text-teal-600 transition-colors"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-slate-200">
            <div className="flex flex-col gap-2 pt-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => handleNavClick(link.path)}
                  className={`px-4 py-3 rounded-md text-base font-medium transition-all duration-300 ${
                    location.pathname === link.path
                      ? 'bg-teal-50 text-teal-600'
                      : 'text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/contact"
                onClick={() => handleNavClick('/contact')}
                className={`px-4 py-3 rounded-md text-base font-medium transition-all duration-300 ${
                  location.pathname === '/contact'
                    ? 'bg-teal-50 text-teal-600'
                    : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                Contact Us
              </Link>
              {/* Mobile Social Icons */}
              <div className="flex gap-4 px-4 pt-4 mt-2 border-t border-slate-200">
                {/* LinkedIn */}
                <a href="https://www.linkedin.com/company/calspark-electrical-and-engineers/" target="_blank" rel="noopener noreferrer" className="text-slate-600 hover:text-teal-600">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                  </svg>
                </a>
                {/* Facebook */}
                <a href="https://www.facebook.com/CalsparkElectricalEngineers/" target="_blank" rel="noopener noreferrer" className="text-slate-600 hover:text-teal-600">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </a>
                {/* YouTube - no link */}
                <span className="text-slate-600">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </span>
                {/* X (formerly Twitter) - no link */}
                <span className="text-slate-600">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
